<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Transform Inventory</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form 
          v-if="projectInventory"
          class="modal-card-body" 
          :valid.sync="valid">

          <div class="field">
            <label class="label">Project*</label>
            <span>{{ projectInventory.project.project_name }}</span>
          </div>

          <div class="columns">
            <div class="column is-half">
              <label class="label">Input Product</label>
              <span>{{ projectInventory.inventory.product_master.name }}</span>
            </div>
            <div class="column is-half">
              <label class="label">Input Product Size*</label>
              <div>
                <span v-if="inputHasDimensions">
                  {{ lwt(projectInventory.inventory.stocking_unit) }}
                </span>
                <span v-else>
                  {{ projectInventory.inventory.product_master.size }}
                </span> 
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Input Quantity*</label>
            <v-text-field
              required
              placeholder="Enter Input Product Quantity"
              type="number"
              v-model="transformation.input_qty"
              :rules="inputQtyRules">
            </v-text-field>
            <span class="help">
              {{projectInventory.qty_available - transformation.input_qty}} available
            </span>
          </div>

          <div class="field">
            <label class="label">Output Product Category*</label>
            <v-select
              required
              :options="categories"
              item-text="category"
              item-value="id"
              v-model="categoryId"
            />
          </div>

          <div class="columns">
            <div class="column is-half">
              <label class="label">Output Product*</label>
              <template v-if="categoryId">
                <v-select
                  v-if="products.length > 0"
                  required  
                  :options="products"
                  item-text="name"
                  item-value="id"
                  v-model="productId"
                />
                <span v-else class="has-text-grey-light">
                  No products in this category
                </span>
              </template>
              <span v-else class="has-text-grey-light">
                Please select a category
              </span>
            </div>

            <div class="column is-half">
              <label class="label">Output Product Size*</label>
              <template v-if="productId">
                <template v-if="outputHasDimensions">
                  <v-select
                    v-if="product.stocking_units.length > 1"
                    required
                    :options="product.stocking_units"
                    :item-text="(v) => { return lwt(v) }"
                    item-value="id"
                    v-model="transformation.output_stocking_unit_id"
                  />
                  <span v-else>
                    {{ lwt(product.stocking_units[0]) }}
                  </span>
                </template>
                <template v-else>
                  <span v-if="product.size">
                    {{ product.size }}
                  </span>
                  <span v-else class="has-text-grey-light">
                    No size available
                  </span>
                </template>
              </template>
              <span v-else class="has-text-grey-light">
                Please select a product
              </span>
            </div>
          </div>

          <div class="field">
            <label class="label">Output Quantity*</label>
            <v-text-field
              required
              placeholder="Enter Output Quantity"
              type="number"
              v-model="transformation.output_qty">
            </v-text-field>
          </div>

        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid"
            class="button is-primary" 
            @click.stop="transform()">
            Confirm Transformation
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'

import { mapState, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    },
    projectInventory: {
      type: Object
    }
  },
  components: { 
    VForm,
    VTextField,
    VSelect
  },
  data() {
    return {
      categoryId: null,
      productId: null,
      transformation: {
        project_id: null,
        input_stocking_unit_id: null,
        input_qty: null,
        output_stocking_unit_id: null,
        output_qty: null
      },
      valid: false
    }
  },
  computed: {
    ...mapState("products", ["categories", "products"]),
    inputQtyRules() {
      return [
        (v) => v <= this.projectInventory.qty_available || 'Input quantity cannot be greater than quantity available'
      ]
    },
    product() {
      return this.products.filter(
        d => d.id == this.productId
      )[0];
    },
    inputHasDimensions() {
      return this.projectInventory.inventory.product_master.product_category.has_dimensions;
    },
    outputHasDimensions() {
      return this.products.filter(
        d => d.id == this.productId
      )[0].product_category.has_dimensions;
    }
  },
  watch: {
    categoryId(id) {
      this.productId = null;
      this.getProducts({ product_category_id: id });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("products", ["getCategories", "getProducts"]),
    ...mapActions("inventoryProject", ["transformInventory"]),
    initialize() {
      if (this.categories.length == 0) {
        this.getCategories();
      }
    },
    transform() {
      this.transformation.project_id = this.projectInventory.project.id;
      this.transformation.input_stocking_unit_id = this.projectInventory.inventory.stocking_unit_id;

      this.transformInventory(this.transformation)
        .then(() => this.close(true));
    },
    lwt(v) {
      return `L${v.length_in_str}" x W${v.width_in_str} x T${v.thick_in_str}`
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>
